import {
  AfterViewInit,
  Component,
  Inject,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { tap } from 'rxjs/operators';
import { AppService } from 'src/app/app.service';
import { ContractorsSelectorComponent } from 'src/app/contractors/contractors/contractors-selector/contractors-selector.component';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { UtilsService } from 'src/app/shared/utils.service';
import { CustomDataSource } from 'src/app/utils/custom-data-source';
import { UserPublicProfileComponent } from '../../shared/user-public-profile/user-public-profile.component';
import { RBACService } from '../../shared/rbac.service';

@Component({
  selector: 'app-employees-selector',
  templateUrl: './employees-selector.component.html',
  styleUrls: ['./employees-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class EmployeesSelectorComponent implements OnInit, AfterViewInit {
  // columns to show and the data source
  displayedColumns: string[] = [
    'select',
    'contact_person',
    'email',
    'mobile',
    'trade'
  ];
  dataSource: EmployeesSelectorDataSource;

  // the paginator and sorter
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    public app: AppService,
    private api: ApiRequestService,
    public dialogRef: MatDialogRef<ContractorsSelectorComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public utils: UtilsService,
    private rbac: RBACService
  ) {
    // set multiple if it is not present to false
    if (typeof this.data['multiple'] === 'undefined') {
      this.data['multiple'] = false;
    }

    // construct the datasource
    this.dataSource = new EmployeesSelectorDataSource(
      this.app,
      this.api,
      this.data['multiple']
    );

    // set a default selection of nothing if no selected values are passed through
    if (typeof this.data['selected'] === 'undefined') {
      this.data['selected'] = [];
    }

    // loop and select the values
    for (let i = 0; i < this.data['selected'].length; i++) {
      this.dataSource.selection.select(this.data['selected'][i]);
    }
  }

  ngOnInit() {
    // Check RBAC permission: standalone employees:view OR parent features that require employee selection
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_EMPLOYEES,
            RBACService.RBAC_A.A_VIEW
          ),
          // Parent features that require employee selection (form field selectors)
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_MANAGERS,
            RBACService.RBAC_A.A_EDIT
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_TA_JSA_SWMS,
            RBACService.RBAC_A.A_CREATE
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_TA_JSA_SWMS,
            RBACService.RBAC_A.A_EDIT
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_TEMPLATES_TA_JSA_SWMS,
            RBACService.RBAC_A.A_CREATE
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_TEMPLATES_TA_JSA_SWMS,
            RBACService.RBAC_A.A_EDIT
          ),
          // Assigning employees to an RBAC role needs this picker, and role membership is governed by
          // the RBAC roles feature alone. View is included because the RBAC roles list filter uses the
          // same picker to filter roles by their assigned employees.
          RBACService.getPermission(
            RBACService.RBAC_F.F_ACCOUNT_RBAC_ROLES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_ACCOUNT_RBAC_ROLES,
            RBACService.RBAC_A.A_EDIT
          )
        ],
        false
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to view employees.'
      );
      this.dialogRef.close();
      return;
    }

    // get the list of sites
    this.dataSource.getData();
  }

  ngAfterViewInit() {
    // reset the paginator when sorting
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.sort_by = this.sort.active;
      this.dataSource.sort_order = this.sort.direction;

      this.dataSource.getData();
    });

    // subscribe to the paginator tap events
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  /**
   * before the dialog close we want to store the new active site id
   */
  onSaveAndClose() {
    this.dialogRef.close(this.dataSource.selection.selected);
  }

  /**
   * Whether a row is a partner user that the current user may not select or deselect. Only applies
   * when the caller opts in with `lockPartnerManaged` (RBAC role members, ui-spec 4.3). Super admins
   * are never locked. A locked row keeps the selection state it was opened with.
   *
   * @param row The employee row (`is_partner_managed` comes from the API).
   * @returns {boolean}
   */
  rowLocked(row: any): boolean {
    return !!this.data['lockPartnerManaged'] && this.rbac.partnerLockApplies(!!row?.is_partner_managed);
  }

  /**
   * Toggle a row's selection, refusing partner rows that are locked for the current user.
   *
   * @param row The employee row.
   */
  onToggleRow(row: any): void {
    if (this.rowLocked(row)) {
      this.rbac.showGenericAccessDeniedMessage(this.rbac.partnerManagedMessage());
      return;
    }
    this.dataSource.selection.toggle(row.user_id);
  }

  onUserPublicView(hash: string, event: Event) {
    // Prevent the default events and propagation from triggering.
    event.preventDefault();
    event.stopPropagation();

    this.utils.showComponentDialog(
      UserPublicProfileComponent,
      hash,
      { width: '90%' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
        // this.dataSource.getData();
      }
    );
  }

  onClose() {
    this.dialogRef.close();
  }
}

export class EmployeesSelectorDataSource extends CustomDataSource {
  // sorting order and direction
  sort_by = 'contact_person';
  sort_order = 'asc';

  getData(resetOffset: boolean = false) {
    this.getDataFromLaravelAPI('employees', resetOffset);
  }
}
